import React from 'react';
import { XCircle, Plus, Minus } from 'lucide-react';
import { ModalityCard } from './ModalityCard';

export const InterventionConfigPanel = ({ 
  intervention, 
  modalities, 
  config, 
  onConfigChange, 
  onClose 
}) => {
  const selectedModality = modalities.find(m => m.id === config.modality_id);
  const baseCost = config.cost_override || intervention.typical_cost_per_person;
  const adjustedCost = baseCost * (selectedModality?.cost_multiplier || 1);
  const totalCost = adjustedCost * config.target_audience_size;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{intervention.name}</h3>
              <p className="text-gray-600">{intervention.description}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <XCircle size={24} className="text-gray-400" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Modality Selection */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Select Delivery Modality</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {modalities.map(modality => (
                <ModalityCard
                  key={modality.id}
                  modality={modality}
                  selected={config.modality_id === modality.id}
                  onClick={() => onConfigChange({ ...config, modality_id: modality.id })}
                />
              ))}
            </div>
          </div>
          
          {/* Audience Size */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Target Audience Size</h4>
            <div className="flex items-center gap-4">
              <button
                onClick={() => onConfigChange({ ...config, target_audience_size: Math.max(10, config.target_audience_size - 50) })}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                <Minus size={20} />
              </button>
              <input
                type="number"
                value={config.target_audience_size}
                onChange={(e) => onConfigChange({ ...config, target_audience_size: parseInt(e.target.value) || 0 })}
                className="w-32 px-4 py-2 border rounded-lg text-center text-xl font-bold"
              />
              <button
                onClick={() => onConfigChange({ ...config, target_audience_size: config.target_audience_size + 50 })}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                <Plus size={20} />
              </button>
              <span className="text-gray-600">employees</span>
            </div>
          </div>
          
          {/* Cost Override */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Cost per Person (Optional Override)</h4>
            <div className="flex items-center gap-4">
              <span className="text-gray-600">$</span>
              <input
                type="number"
                value={config.cost_override || ''}
                onChange={(e) => onConfigChange({ ...config, cost_override: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder={baseCost.toString()}
                className="w-32 px-4 py-2 border rounded-lg"
              />
              <span className="text-sm text-gray-500">
                Default: ${baseCost.toLocaleString()} × {selectedModality?.cost_multiplier || 1}x = ${adjustedCost.toLocaleString()}
              </span>
            </div>
          </div>
          
          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Configuration Summary</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-gray-500">Modality</span>
                <p className="font-medium">{selectedModality?.name || 'Not selected'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Effectiveness</span>
                <p className="font-medium text-emerald-600">{((selectedModality?.effectiveness || 0) * 100).toFixed(0)}%</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Total Investment</span>
                <p className="font-bold text-blue-600">${totalCost.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
